import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { config } from '@/app/config';
import { useAppStore } from '@/app/store';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { getSupabase } from '@/supabase/client';
import { persistSupabaseSession, spaceRepo } from '@/repositories';
import { classifyJoinError } from '@/domain/spaceErrors';
import styles from './AuthPage.module.css';

type AuthMode = 'signIn' | 'signUp' | 'forgot';

function mapJoinError(err: unknown, t: (k: string) => string): string {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  switch (classifyJoinError(msg)) {
    case 'expired':
      return t('app.inviteExpired');
    case 'full':
      return t('app.spaceFull');
    case 'already_in_space':
      return t('auth.errors.alreadyMember');
    case 'cannot_leave':
      return t('auth.errors.cannotLeaveForInvite');
    case 'invalid':
      return t('auth.errors.invalidInvite');
    default:
      return msg.trim() ? msg : t('auth.errors.generic');
  }
}

export function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { code } = useParams();
  const loginDemo = useAppStore((s) => s.loginDemo);
  const refreshSpaceData = useAppStore((s) => s.refreshSpaceData);
  const session = useAppStore((s) => s.session);

  const [mode, setMode] = useState<AuthMode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [joinCode, setJoinCode] = useState(code ?? '');
  const [authError, setAuthError] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showJoin, setShowJoin] = useState(Boolean(code));

  const supabase = getSupabase();

  const handleDemo = async (who: 'alex' | 'mia') => {
    setLoading(true);
    try {
      await loginDemo(who);
      navigate('/app', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const tryJoinAfterAuth = async (userId: string) => {
    const trimmed = joinCode.trim();
    if (!trimmed) return;
    await spaceRepo.joinByCode(userId, trimmed);
    await refreshSpaceData();
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    setAuthError(null);
    setJoinError(null);
    setMessage(null);
    setLoading(true);
    try {
      if (mode === 'forgot') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${config.appUrl}/app/auth`,
        });
        if (resetError) throw resetError;
        setMessage(t('auth.resetSent'));
        return;
      }

      if (mode === 'signUp') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${config.appUrl}/app/auth`,
          },
        });
        if (signUpError) throw signUpError;
        if (data.session) {
          const authSession = await persistSupabaseSession(data.session);
          useAppStore.setState({ session: authSession });
          try {
            await tryJoinAfterAuth(authSession.user.id);
          } catch (joinErr) {
            setShowJoin(true);
            setJoinError(mapJoinError(joinErr, t));
            await refreshSpaceData();
            navigate('/app', { replace: true });
            return;
          }
          await refreshSpaceData();
          navigate('/app', { replace: true });
          return;
        }
        setMessage(t('auth.confirmEmail'));
        return;
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
      if (data.session) {
        const authSession = await persistSupabaseSession(data.session);
        useAppStore.setState({ session: authSession });
        try {
          await tryJoinAfterAuth(authSession.user.id);
        } catch (joinErr) {
          if (joinCode.trim()) {
            setShowJoin(true);
            setJoinError(mapJoinError(joinErr, t));
          }
          await refreshSpaceData();
          navigate('/app', { replace: true });
          return;
        }
        await refreshSpaceData();
        navigate('/app', { replace: true });
      }
    } catch {
      setAuthError(t('auth.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setJoinError(null);
    setLoading(true);
    try {
      const current = useAppStore.getState().session;
      if (!current) {
        setJoinError(t('auth.errors.signInFirst'));
        setShowJoin(true);
        return;
      }
      await spaceRepo.joinByCode(current.user.id, joinCode);
      await refreshSpaceData();
      navigate('/app', { replace: true });
    } catch (err) {
      setJoinError(mapJoinError(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Link to="/" className={styles.logoLink}>
          <Logo size="md" />
        </Link>

        {config.mode === 'demo' ? (
          <>
            <h1 className={styles.title}>{t('auth.demoTitle')}</h1>
            <p className={styles.subtitle}>{t('auth.demoSubtitle')}</p>
            <div className={styles.demoBtns}>
              <Button variant="primary" loading={loading} onClick={() => void handleDemo('alex')}>
                {t('auth.enterAsAlex')}
              </Button>
              <Button variant="secondary" loading={loading} onClick={() => void handleDemo('mia')}>
                {t('auth.enterAsMia')}
              </Button>
            </div>
          </>
        ) : !supabase ? (
          <>
            <h1 className={styles.title}>{t('errors.configTitle')}</h1>
            <p className={styles.subtitle}>{t('auth.supabaseUnavailable')}</p>
          </>
        ) : (
          <>
            <h1 className={styles.title}>
              {mode === 'signUp' ? t('auth.createAccount') : mode === 'forgot' ? t('auth.resetPassword') : t('auth.signIn')}
            </h1>
            <form className={styles.form} onSubmit={(e) => void handleEmailAuth(e)}>
              <Input
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              {mode !== 'forgot' && (
                <Input
                  label={t('auth.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
                />
              )}
              {authError && <p className={styles.error}>{authError}</p>}
              {message && <p className={styles.message}>{message}</p>}
              <Button type="submit" variant="primary" loading={loading} className={styles.submit}>
                {mode === 'forgot' ? t('auth.resetPassword') : mode === 'signUp' ? t('auth.createAccount') : t('auth.signIn')}
              </Button>
            </form>
            <div className={styles.switch}>
              {mode === 'signIn' && (
                <>
                  <button type="button" className={styles.linkBtn} onClick={() => setMode('signUp')}>
                    {t('auth.noAccount')}
                  </button>
                  <button type="button" className={styles.linkBtn} onClick={() => setMode('forgot')}>
                    {t('auth.forgotPassword')}
                  </button>
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => {
                      setShowJoin(true);
                      setJoinError(null);
                    }}
                  >
                    {t('onboarding.joinTitle')}
                  </button>
                </>
              )}
              {mode !== 'signIn' && (
                <button type="button" className={styles.linkBtn} onClick={() => setMode('signIn')}>
                  {t('auth.alreadyHaveAccount')}
                </button>
              )}
            </div>
          </>
        )}

        {(showJoin || code || joinCode) && (config.mode === 'demo' || session || config.mode === 'production') && (
          <form className={styles.joinForm} onSubmit={(e) => void handleJoin(e)}>
            <h2 className={styles.joinTitle}>{t('onboarding.joinTitle')}</h2>
            <p className={styles.subtitle}>{t('onboarding.joinHint')}</p>
            {!session && config.mode === 'production' && (
              <p className={styles.error}>{t('auth.errors.signInFirst')}</p>
            )}
            <Input
              label={t('onboarding.inviteCode')}
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            />
            {joinError && <p className={styles.error} role="alert">{joinError}</p>}
            <Button type="submit" variant="secondary" loading={loading} disabled={config.mode === 'production' && !session}>
              {t('onboarding.joinConfirm')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
